/**
 * Centralized Error Handling Service
 *
 * This service provides:
 * - Consistent error formatting across the app
 * - Axios interceptors for API error handling
 * - User-friendly error messages
 * - Error logging for debugging
 * - Sentry integration (when available)
 *
 * Usage:
 *   import { setupErrorHandling, handleError, ApiError } from '@/services/errorHandler'
 *
 *   // In main.js or app initialization:
 *   setupErrorHandling(store)
 *
 *   // In components:
 *   try {
 *     await someApiCall()
 *   } catch (error) {
 *     const handled = handleError(error, 'Saving settings')
 *     this.errorMessage = handled.userMessage
 *   }
 */

import logger from '@/utils/logger'

// Error code mapping for user-friendly messages
const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  TIMEOUT: 'The request timed out. Please try again.',

  // HTTP status codes
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'There was a conflict with the current state. Please refresh and try again.',
  422: 'The submitted data was invalid. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An unexpected server error occurred. Our team has been notified.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
  504: 'The server took too long to respond. Please try again.',

  // Default
  DEFAULT: 'An unexpected error occurred. Please try again.'
}

// Store reference for dispatching actions
let appStore = null

/**
 * Custom API Error class with additional context
 */
export class ApiError extends Error {
  constructor(message, { status, code, originalError, context, userMessage } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.originalError = originalError
    this.context = context
    this.userMessage = userMessage || getUserFriendlyMessage(status, code, message)
    this.timestamp = new Date().toISOString()
  }
}

/**
 * Get user-friendly error message based on error details
 */
function getUserFriendlyMessage(status, code, serverMessage) {
  // Check for specific error codes first
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code]
  }

  // Check for HTTP status code
  if (status && ERROR_MESSAGES[status]) {
    return ERROR_MESSAGES[status]
  }

  // If server provided a clear message, use it (but sanitize)
  if (serverMessage && serverMessage.length < 200 && !serverMessage.includes('Exception')) {
    return serverMessage
  }

  return ERROR_MESSAGES.DEFAULT
}

/**
 * Log error with context for debugging
 */
function logError(error, context = '') {
  const errorDetails = {
    context,
    message: error.message,
    status: error.status || error.response?.status,
    code: error.code,
    url: error.config?.url,
    method: error.config?.method,
    timestamp: new Date().toISOString()
  }

  // Use warn for HTTP errors (4xx/5xx) — server/API errors are not client-side bugs
  const isHttpError = errorDetails.status && errorDetails.status >= 400
  if (isHttpError) {
    logger.warn(`[ErrorHandler] ${context}:`, errorDetails)
  } else {
    logger.error(`[ErrorHandler] ${context}:`, errorDetails)
  }

  // Log to Sentry if available
  if (window.Sentry) {
    window.Sentry.captureException(error, {
      tags: { context },
      extra: errorDetails
    })
  }
}

/**
 * Handle and transform errors into a consistent format
 *
 * @param {Error} error - The error to handle
 * @param {string} context - Description of what operation failed (e.g., "Fetching products")
 * @returns {ApiError} Formatted error with user-friendly message
 */
export function handleError(error, context = 'Operation') {
  // Already handled
  if (error instanceof ApiError) {
    logError(error, context)
    return error
  }

  let apiError

  // Axios error with response
  if (error.response) {
    const { status, data } = error.response
    const serverMessage = data?.Message || data?.message || data?.error || ''

    apiError = new ApiError(serverMessage || error.message, {
      status,
      code: data?.ErrorCode || data?.errorCode,
      originalError: error,
      context,
      userMessage: getUserFriendlyMessage(status, data?.ErrorCode, serverMessage)
    })
  }
  // Axios error without response (network error, timeout, etc.)
  else if (error.request) {
    const code = error.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK_ERROR'
    apiError = new ApiError(error.message, {
      code,
      originalError: error,
      context,
      userMessage: ERROR_MESSAGES[code]
    })
  }
  // Generic JavaScript error
  else {
    apiError = new ApiError(error.message, {
      originalError: error,
      context
    })
  }

  logError(apiError, context)
  return apiError
}

/**
 * Create Axios response interceptor for error handling
 */
function createResponseInterceptor(axiosInstance, instanceName) {
  axiosInstance.interceptors.response.use(
    // Success handler - pass through
    (response) => response,

    // Error handler
    (error) => {
      const context = `${instanceName} ${error.config?.method?.toUpperCase() || 'REQUEST'} ${error.config?.url || 'unknown'}`
      const apiError = handleError(error, context)

      // Handle specific status codes globally
      if (apiError.status === 401 && appStore) {
        // Only treat as "session expired" if user was actually logged in.
        // Avoids showing the toast to unauthenticated guests who get a 401
        // on incidental API calls (e.g. post-purchase background requests).
        //
        // Balance/declining-balance endpoints return 401 when the account lookup
        // fails (wrong location, no linked account) — this is NOT a session expiry.
        // Don't log the user out in that case.
        const requestUrl = apiError.originalError?.config?.url || ''
        const isBalanceEndpoint = /(^|\/)balance\b|(^|\/)declining[\s-]?balance\b/i.test(requestUrl)
          || /\bbalance\b/i.test(context)
        const wasLoggedIn = appStore.state.loggedIn === true
        if (wasLoggedIn && !isBalanceEndpoint) {
          logger.warn('[ErrorHandler] Session expired, logging out user')
          appStore.dispatch('logout')

          if (appStore._vm?.$toast) {
            appStore._vm.$toast.error('Session Expired', 'Please log in again.')
          }
        } else if (wasLoggedIn && isBalanceEndpoint) {
          logger.warn('[ErrorHandler] Balance endpoint returned 401 — skipping logout (account/location mismatch)')
        }
      }

      // Reject with the enhanced error
      return Promise.reject(apiError)
    }
  )
}

/**
 * Create Axios request interceptor for logging
 */
function createRequestInterceptor(axiosInstance, instanceName) {
  axiosInstance.interceptors.request.use(
    (config) => {
      // Add request timestamp for timing
      config.metadata = { startTime: Date.now() }

      logger.debug(`[${instanceName}] ${config.method?.toUpperCase()} ${config.url}`)
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )
}

/**
 * Setup error handling for all Axios instances
 * Call this in main.js after config is loaded
 *
 * @param {Object} store - Vuex store instance
 */
export function setupErrorHandling(store) {
  appStore = store

  // Import axios instances
  const { HTTP, HTTPImage } = require('./repository')
  const { ECommerceAPI } = require('./ecommerceApi')

  // Setup interceptors for each instance
  const instances = [
    { instance: HTTP, name: 'HTTP' },
    { instance: HTTPImage, name: 'HTTPImage' },
    { instance: ECommerceAPI, name: 'ECommerceAPI' }
  ]

  instances.forEach(({ instance, name }) => {
    if (instance) {
      createRequestInterceptor(instance, name)
      createResponseInterceptor(instance, name)
      logger.debug(`[ErrorHandler] Interceptors attached to ${name}`)
    }
  })

  // Setup global error handler for uncaught promises
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason
    if (error instanceof ApiError) {
      // Already handled
      return
    }

    logger.error('[ErrorHandler] Unhandled promise rejection:', error)

    // Log to Sentry
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        tags: { type: 'unhandledRejection' }
      })
    }
  })

  logger.info('[ErrorHandler] Centralized error handling initialized')
}

/**
 * Utility to wrap async functions with error handling
 *
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context description for error messages
 * @returns {Function} Wrapped function that handles errors
 */
export function withErrorHandling(fn, context) {
  return async (...args) => {
    try {
      return await fn(...args)
    } catch (error) {
      throw handleError(error, context)
    }
  }
}

/**
 * Vue mixin for component-level error handling
 */
export const errorHandlerMixin = {
  methods: {
    /**
     * Handle an error and optionally show notification
     * @param {Error} error - The error to handle
     * @param {string} context - Context for the error
     * @param {boolean} showNotification - Whether to show toast notification
     * @returns {ApiError} The handled error
     */
    $handleError(error, context = 'Operation', showNotification = true) {
      const apiError = handleError(error, context)

      if (showNotification && this.$toast) {
        this.$toast.error(context + ' Failed', apiError.userMessage)
      }

      return apiError
    }
  }
}

export default {
  ApiError,
  handleError,
  setupErrorHandling,
  withErrorHandling,
  errorHandlerMixin
}
